import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import type { ReactNode } from "react";
import { useId } from "react";
import cx from "ui/cx";
import Label from "ui/forms/Label";
import { useOneClickPerActivation } from "ui/forms/oneClickPerActivation";
import IconCheck from "ui/icons/IconCheck";
import IconDash from "ui/icons/IconDash";
import IconLocked from "ui/icons/IconLocked";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./Checkbox.module.scss";

/** `render` and `nativeButton` are withheld for the reason given on `SwitchProps`. */
export interface CheckboxProps
  extends Omit<
    React.ComponentProps<typeof CheckboxPrimitive.Root>,
    "checked" | "onCheckedChange" | "indeterminate" | "render" | "nativeButton"
  > {
  label?: ReactNode;
  locked?: boolean;
  readOnly?: boolean;
  className?: string;
  // Supports the tri-state (boolean | "indeterminate") API the wrapper has always exposed for its
  // input, mapped onto Base UI's separate `checked` + `indeterminate` props internally. The change
  // callback only ever emits a boolean (toggling never produces "indeterminate").
  checked?: boolean | "indeterminate";
  onCheckedChange?: (checked: boolean) => void;
  onChangeBoolean?: (checked: boolean) => void;
  tooltip?: ReactNode;
  labelClassName?: string;
}

export default function Checkbox({
  label,
  locked = false,
  readOnly = false,
  disabled = false,
  className,
  labelClassName,
  checked,
  onChangeBoolean, // use for boolean state only
  onCheckedChange, // use for full state w/ indeterminate
  tooltip,
  ...props
}: CheckboxProps) {
  const id = useId();
  const isDisabled = disabled || locked || readOnly;
  const isIndeterminate = checked === "indeterminate";

  const handleCheckedChange = (newChecked: boolean) => {
    if (!isDisabled) {
      onCheckedChange?.(newChecked);
      if (!onCheckedChange) {
        onChangeBoolean?.(newChecked);
      }
    }
  };

  const controlled = !onCheckedChange && !onChangeBoolean;

  // Collapses Base UI's real-plus-re-dispatched click pair down to one per press for ancestors.
  const oneClickPerActivation = useOneClickPerActivation();

  return wrapWithTooltip(
    <div
      className={cx(
        styles.wrapper,
        isDisabled && styles.disabledBase,
        disabled && styles.disabled,
        locked && styles.locked,
        readOnly && styles.readOnly,
        className,
      )}
      data-testid="checkbox-wrapper"
      {...oneClickPerActivation}
    >
      <CheckboxPrimitive.Root
        id={id}
        checked={locked || isIndeterminate ? false : checked}
        indeterminate={locked ? false : isIndeterminate}
        onCheckedChange={handleCheckedChange}
        disabled={isDisabled}
        className={styles.root}
        data-testid="checkbox-input"
        {...props}
      >
        <CheckboxPrimitive.Indicator className={styles.indicator} data-testid="checkbox-indicator">
          {isIndeterminate ? <IconDash size={12} /> : <IconCheck size={12} strokeWidth={2} />}
        </CheckboxPrimitive.Indicator>
        {locked && <IconLocked className={styles.lockIcon} data-testid="checkbox-lock-icon" />}
      </CheckboxPrimitive.Root>
      {label && (
        <Label
          // if controlled (no onChange handlers), don't set the htmlFor attribute so that the label is not clickable
          htmlFor={!controlled ? id : undefined}
          disabled={isDisabled}
          data-testid="checkbox-label"
          tooltip={tooltip}
          className={labelClassName}
        >
          {label}
        </Label>
      )}
    </div>,
    // if no label, put the tooltip on the checkbox
    tooltip && !label ? tooltip : undefined,
  );
}
